import {DialogsEnum} from "@/lib/enums/DialogsEnum";

/**
 * Dialog presentation behaviour when a request is admitted while other
 * dialogs are already visible.
 *
 * - `stack`   — render on top of the current dialogs (e.g. CONFIRM over WALLET).
 * - `queue`   — wait until the current blocking dialog closes.
 * - `replace` — force-close everything below and take over (e.g. session expired).
 */
export type DialogMode = "stack" | "queue" | "replace";

/**
 * Coarse layer used by the z-index manager to guarantee that some classes of
 * dialogs are *always* rendered above others, regardless of insertion order.
 *
 * Higher number === closer to the user. Explicit `zIndex` on a config always
 * wins over the layer-derived value.
 */
export enum DialogLayer {
    CONTENT = 0,   // regular feature dialogs (wallet, search…)
    FLOW = 1,      // multi-step flows (deposit, bonus claim…)
    MARKETING = 2, // promos — intentionally low so anything can preempt them
    AUTH = 3,      // auth / security gates — above marketing & content
    SYSTEM = 4,    // system notifications
    CRITICAL = 5,  // confirm / destructive / session-expired — top-most
}

/**
 * Logical grouping used by flow locks and analytics. Lets a flow lock express
 * rules like "block all marketing while a deposit is in progress" without
 * enumerating individual dialog ids.
 */
export type DialogCategory =
    | "system"
    | "auth"
    | "flow"
    | "marketing"
    | "bonus"
    | "wallet"
    | "utility";

/** Where a dialog request originated — purely informational / for analytics. */
export type DialogSource =
    | "user"
    | "websocket"
    | "api"
    | "timer"
    | "marketing"
    | "auth"
    | "system"
    | "dependency";

/**
 * Runtime context the orchestrator evaluates every decision against. It is a
 * *snapshot* produced by the host app (Redux + router) and pushed into the
 * engine — the engine never reaches into Redux directly.
 */
export interface OrchestratorContext {
    route: string;
    isAuthenticated: boolean;
    activeFlow?: "deposit" | "bonus" | "auth" | null;
    /** Ids of dialogs currently on screen, top-most last. */
    activeDialogs: DialogsEnum[];
    userRole?: string;
    isMobile: boolean;
    isPwaInstalled: boolean;
    hasPendingDeposit: boolean;
    /** Escape hatch for app-specific signals without widening the core type. */
    extra?: Record<string, unknown>;
}

/**
 * Static, declarative definition of a dialog. Lives in the registry. Pure data
 * + pure functions — no React, no side effects beyond the lifecycle hooks.
 */
export interface DialogConfig {
    id: DialogsEnum;

    /** Hard z-index override. Prefer `layer` and let the manager allocate. */
    zIndex?: number;
    layer?: DialogLayer;
    category?: DialogCategory;

    /** Default admission mode; can be overridden per-request. */
    mode?: DialogMode;

    /**
     * A blocking dialog prevents lower/equal-layer `queue` dialogs from opening
     * until it closes. Higher-layer dialogs may still stack on top of it.
     */
    blocking?: boolean;

    /** Survives route changes (not auto-closed by RouteObserver). */
    persistent?: boolean;

    /** Minimum time between two displays, persisted across sessions. */
    cooldownMs?: number;
    /** Display cap. Scope defaults to `session`. */
    maxDisplays?: number;
    maxDisplaysScope?: "session" | "lifetime";

    requiresAuth?: boolean;
    allowedRoutes?: (string | RegExp)[];
    forbiddenRoutes?: (string | RegExp)[];
    allowedRoles?: string[];

    /** Other dialogs that must be satisfied/closed-successfully first. */
    dependencies?: DialogsEnum[];

    /** Skip enqueue if an instance of the same id is already queued/active. */
    dedupe?: boolean;

    /**
     * Context-aware priority. Higher wins. Evaluated at decision time so it can
     * react to route, flow, auth, etc. Defaults to 0.
     */
    priority?: (ctx: OrchestratorContext, request: DialogRequest) => number;

    /** Final gate. Returning false keeps the request queued (re-evaluated later). */
    canShow?: (ctx: OrchestratorContext, request: DialogRequest) => boolean;

    /** Awaited before the dialog becomes visible (fetch data, warm up, …). */
    onBeforeOpen?: (request: DialogRequest, ctx: OrchestratorContext) => Promise<void> | void;
    /** Awaited after the dialog is removed (cleanup, analytics, …). */
    onAfterClose?: (result: DialogResult, ctx: OrchestratorContext) => Promise<void> | void;
}

/** Lifecycle phase of a single request instance. */
export type DialogPhase =
    | "requested"
    | "queued"
    | "waiting-dependency"
    | "opening"
    | "open"
    | "closing"
    | "closed"
    | "cancelled"
    | "replaced";

/**
 * A concrete, runtime instance of a "please show this dialog" request. The same
 * `id` can be requested many times; each gets a unique `instanceId`.
 */
export interface DialogRequest {
    id: DialogsEnum;
    instanceId: string;
    props?: Record<string, unknown>;
    mode?: DialogMode;
    source?: DialogSource;

    enqueuedAt: number;
    /** now + delay; the request is not eligible until this time. */
    scheduledFor: number;

    phase: DialogPhase;
    /** Ids this instance is still waiting on (dependency resolution). */
    pendingDependencies?: DialogsEnum[];
    meta?: Record<string, unknown>;
}

/** How a dialog finished — surfaced to `onAfterClose` and the open() promise. */
export interface DialogResult {
    id: DialogsEnum;
    instanceId: string;
    /** true = confirmed/completed, false = dismissed/cancelled. */
    success: boolean;
    reason: "user" | "replaced" | "cancelled" | "programmatic" | "dependency-failed";
    data?: unknown;
}

/** A dialog that is currently on screen, with its resolved layering. */
export interface VisibleDialog {
    id: DialogsEnum;
    instanceId: string;
    zIndex: number;
    props?: Record<string, unknown>;
    blocking: boolean;
    focused: boolean;
}

/** Options accepted by `orchestrator.open(...)`. */
export interface OpenOptions {
    props?: Record<string, unknown>;
    mode?: DialogMode;
    /** ms to wait before the request becomes eligible. */
    delay?: number;
    source?: DialogSource;
    meta?: Record<string, unknown>;
    /** Provide a stable instanceId (useful for idempotent ws/api triggers). */
    instanceId?: string;
}

/** All lifecycle events emitted by the engine. */
export type DialogEventType =
    | "requested"
    | "queued"
    | "opened"
    | "focused"
    | "hidden"
    | "replaced"
    | "closed"
    | "cancelled"
    | "blocked"
    | "flow-locked"
    | "flow-unlocked";

export interface DialogEvent {
    type: DialogEventType;
    id?: DialogsEnum;
    instanceId?: string;
    flow?: string;
    ctx: OrchestratorContext;
    result?: DialogResult;
    at: number;
}

export type DialogEventListener = (event: DialogEvent) => void;

/** Function the host installs so the engine can read fresh context on demand. */
export type ContextProvider = () => OrchestratorContext;

/** Function the engine calls whenever the set of visible dialogs changes. */
export type RenderSink = (visible: VisibleDialog[]) => void;